<template>
  <!--
    По ТЗ 03.09.2026 (4 раздел КПС «В расходе»).
    Управление интервалами отсутствия военнослужащих. Одна запись = один
    интервал (date_from..date_to, date_to может быть пустой — открытая
    командировка/госпиталь). На дату книги учитывается пересечение интервала.
  -->
  <div v-if="open" class="am__backdrop" @mousedown.self="onClose">
    <div class="am" role="dialog" aria-modal="true" @mousedown.stop>
      <header class="am__head">
        <h3 class="am__title">{{ $t('kps.absences.title') }}</h3>
        <button class="am__close" @click="onClose" :aria-label="$t('common.close')">×</button>
      </header>

      <div class="am__toolbar">
        <div class="am__filter">
          <label class="am__filter-label">{{ $t('kps.absences.activeOn') }}</label>
          <DatePicker v-model="activeOn" @update:modelValue="fetch" />
        </div>
        <ActionButton :color="'#2A8A1B'" :text="$t('kps.absences.addBtn')" @click="onAdd" />
      </div>

      <div class="am__body">
        <div v-if="loading" class="am__state">{{ $t('common.loadingData') }}</div>
        <div v-else-if="error" class="am__state am__state--err">{{ error }}</div>
        <table v-else class="am__table">
          <thead>
            <tr>
              <th>{{ $t('kps.absences.colFio') }}</th>
              <th>{{ $t('kps.absences.colReason') }}</th>
              <th>{{ $t('kps.absences.colFrom') }}</th>
              <th>{{ $t('kps.absences.colTo') }}</th>
              <th>{{ $t('kps.absences.colComment') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!rows.length"><td colspan="6" class="am__empty">{{ $t('common.noRecordsForDate') }}</td></tr>
            <tr v-for="r in rows" :key="r.id">
              <template v-if="editingId === r.id">
                <td>
                  <SelectField v-model="draft.user_id" :options="userOptions" :searchable="true" :placeholder="$t('kps.absences.userPh')" />
                </td>
                <td>
                  <SelectField v-model="draft.reason" :options="reasonOptions" :placeholder="$t('kps.absences.reasonPh')" />
                </td>
                <td><DatePicker v-model="draft.date_from" /></td>
                <td><DatePicker v-model="draft.date_to" /></td>
                <td><input class="am__input" v-model="draft.comment" /></td>
                <td class="am__actions">
                  <button class="am__btn am__btn--ok" @click="save" :title="$t('common.save')">✓</button>
                  <button class="am__btn am__btn--cancel" @click="cancelEdit" :title="$t('common.cancel')">×</button>
                </td>
              </template>
              <template v-else>
                <td>{{ fioOf(r) }}</td>
                <td>{{ reasonLabel(r.reason) }}</td>
                <td>{{ fmtDate(r.date_from) }}</td>
                <td>{{ r.date_to ? fmtDate(r.date_to) : '—' }}</td>
                <td class="am__comment">{{ r.comment }}</td>
                <td class="am__actions">
                  <button class="am__btn am__btn--edit" @click="onEdit(r)" :title="$t('common.edit')">✎</button>
                  <button class="am__btn am__btn--del" @click="onDelete(r)" :title="$t('common.delete')">🗑</button>
                </td>
              </template>
            </tr>
            <tr v-if="creating">
              <td>
                <SelectField v-model="draft.user_id" :options="userOptions" :searchable="true" :placeholder="$t('kps.absences.userPh')" />
              </td>
              <td>
                <SelectField v-model="draft.reason" :options="reasonOptions" :placeholder="$t('kps.absences.reasonPh')" />
              </td>
              <td><DatePicker v-model="draft.date_from" /></td>
              <td><DatePicker v-model="draft.date_to" /></td>
              <td><input class="am__input" v-model="draft.comment" /></td>
              <td class="am__actions">
                <button class="am__btn am__btn--ok" @click="save" :title="$t('common.save')">✓</button>
                <button class="am__btn am__btn--cancel" @click="cancelEdit" :title="$t('common.cancel')">×</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/widgets/DatePicker.vue'
import SelectField from '@/components/widgets/SelectField.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import { listAbsencesOn, createAbsence, updateAbsence, deleteAbsence, ABSENCE_REASONS } from '@/api/absences'
import { listBookUsers } from '@/api/bookUsers'

export default {
  name: 'AbsencesModal',
  components: { DatePicker, SelectField, ActionButton },
  props: {
    open: { type: Boolean, default: false },
    dateISO: { type: String, default: '' },
  },
  emits: ['close', 'changed'],
  data() {
    return {
      activeOn: this.dateISO || this.todayISO(),
      loading: false,
      error: null,
      rows: [],
      users: [],
      creating: false,
      editingId: null,
      draft: this.emptyDraft(),
    }
  },
  computed: {
    reasonOptions() {
      const kk = this.$i18n?.locale === 'kz'
      return ABSENCE_REASONS.map(r => ({ value: r.value, label: kk ? r.labelKk : r.labelRu }))
    },
    userOptions() {
      return this.users.map(u => ({
        value: u.user?.id || u.user_id,
        label: `${u.personal_number ? '№' + u.personal_number + ' ' : ''}${u.fio || u.full_name || ''}`.trim(),
      })).filter(x => x.value)
    },
  },
  watch: {
    open(v) { if (v) { this.activeOn = this.dateISO || this.todayISO(); this.fetch(); this.loadUsers() } },
    dateISO(v) { if (v && this.open) { this.activeOn = v; this.fetch() } },
  },
  methods: {
    todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` },
    emptyDraft() { return { user_id: null, reason: 'leave', date_from: '', date_to: '', comment: '' } },
    fmtDate(iso) { if (!iso) return ''; const [y,m,d] = iso.split('-'); return `${d}.${m}.${y}` },
    fioOf(r) { return r.user?.full_name || r.user?.fio || '—' },
    reasonLabel(code) { const r = ABSENCE_REASONS.find(x => x.value === code); if (!r) return code; return this.$i18n?.locale === 'kz' ? r.labelKk : r.labelRu },
    onClose() { this.editingId = null; this.creating = false; this.$emit('close') },
    async fetch() {
      this.loading = true; this.error = null
      try {
        this.rows = await listAbsencesOn(this.activeOn)
      } catch (e) {
        this.error = e?.message || 'Load failed'
      } finally { this.loading = false }
    },
    async loadUsers() {
      try { this.users = await listBookUsers({ is_archived: false }) } catch (_) { this.users = [] }
    },
    onAdd() {
      this.editingId = null
      this.creating = true
      this.draft = { ...this.emptyDraft(), date_from: this.activeOn }
    },
    onEdit(r) {
      this.creating = false
      this.editingId = r.id
      this.draft = {
        user_id: r.user?.id || r.user_id,
        reason: r.reason,
        date_from: r.date_from,
        date_to: r.date_to || '',
        comment: r.comment || '',
      }
    },
    cancelEdit() { this.editingId = null; this.creating = false; this.draft = this.emptyDraft() },
    async save() {
      if (!this.draft.user_id || !this.draft.date_from) { this.error = this.$t('common.fillRequired'); return }
      const payload = {
        user_id: this.draft.user_id,
        reason: this.draft.reason,
        date_from: this.draft.date_from,
        date_to: this.draft.date_to || null,
        comment: this.draft.comment,
      }
      try {
        if (this.editingId) await updateAbsence(this.editingId, payload)
        else await createAbsence(payload)
        this.cancelEdit()
        await this.fetch()
        this.$emit('changed')
      } catch (e) {
        this.error = e?.response?.data ? JSON.stringify(e.response.data) : (e.message || 'Save failed')
      }
    },
    async onDelete(r) {
      if (!confirm(this.$t('kps.absences.deleteConfirm'))) return
      try { await deleteAbsence(r.id); await this.fetch(); this.$emit('changed') }
      catch (e) { this.error = e?.message || 'Delete failed' }
    },
  },
}
</script>

<style scoped>
.am__backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.am { background: #fff; border-radius: 12px; width: min(1000px, 96vw); max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,.25); }
.am__head { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #E5E7EB; }
.am__title { margin: 0; font-size: 18px; font-weight: 600; color: #111827; }
.am__close { background: none; border: 0; font-size: 24px; cursor: pointer; color: #6B7280; }
.am__toolbar { display: flex; justify-content: space-between; align-items: center; padding: 12px 20px; gap: 16px; border-bottom: 1px solid #F3F4F6; }
.am__filter { display: flex; align-items: center; gap: 8px; }
.am__filter-label { font-size: 13px; color: #374151; }
.am__body { padding: 12px 20px 20px; overflow: auto; }
.am__state { padding: 16px; text-align: center; color: #6B7280; }
.am__state--err { color: #DC2626; }
.am__table { width: 100%; border-collapse: collapse; font-size: 14px; }
.am__table th { text-align: left; font-weight: 600; padding: 10px 8px; border-bottom: 1px solid #E5E7EB; color: #374151; background: #F9FAFB; position: sticky; top: 0; }
.am__table td { padding: 8px; border-bottom: 1px solid #F3F4F6; vertical-align: middle; }
.am__empty { text-align: center; padding: 24px; color: #9CA3AF; }
.am__input { width: 100%; padding: 6px 8px; border: 1px solid #D1D5DB; border-radius: 6px; font-size: 13px; }
.am__comment { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am__actions { display: flex; gap: 6px; }
.am__btn { border: 0; background: transparent; cursor: pointer; padding: 6px 8px; border-radius: 6px; font-size: 14px; }
.am__btn--edit { color: #2563EB; }
.am__btn--del { color: #DC2626; }
.am__btn--ok { color: #2A8A1B; background: #ECFDF5; }
.am__btn--cancel { color: #6B7280; }
.am__btn:hover { background: #F3F4F6; }
</style>
